/**
 * TokenValidator - Centralizador de validação de tokens
 *
 * Consolida toda a lógica de validação de sessionToken e HMAC key
 * que estava duplicada em múltiplos arquivos (ToolsMenu.tsx, main.tsx, CorelCommunication.ts)
 *
 * @author Simplificação Fase 1 - 2026-01-13
 */

/**
 * Resultado de validação de token
 */
export interface ValidationResult {
  valid: boolean;
  format?: 'GUID' | 'Hex64' | 'invalid';
  length?: number;
  error?: string;
}

/**
 * Diagnóstico completo de tokens
 */
export interface TokenDiagnostics {
  sessionToken: {
    exists: boolean;
    valid: boolean;
    format: string;
    length: number;
    preview: string;
  };
  hmacKey: {
    exists: boolean;
    valid: boolean;
    isBase64: boolean;
    length: number;
    preview: string;
  };
  source: 'message' | 'dom' | 'unknown';
}

/**
 * Classe centralizada para validação de tokens
 */
export class TokenValidator {
  // Regex patterns
  private static readonly GUID_REGEX = /^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$/;
  private static readonly HEX64_REGEX = /^[a-fA-F0-9]+$/;
  private static readonly BASE64_REGEX = /^[A-Za-z0-9+/=]+$/;

  /**
   * Valida sessionToken (aceita GUID 36 chars ou Hex 64 chars)
   *
   * Formatos aceitos:
   * - GUID (36 chars): xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
   * - Hex (64 chars): a1b2c3d4e5f6... (legado)
   *
   * @param token Token a ser validado
   * @returns Resultado da validação com formato detectado
   */
  static validateSessionToken(token: string | null | undefined): ValidationResult {
    // Verificar se existe
    if (!token) {
      return {
        valid: false,
        error: 'Token ausente'
      };
    }

    const str = String(token).trim();

    // Verificar se está vazio
    if (str.length === 0) {
      return {
        valid: false,
        length: 0,
        error: 'Token vazio'
      };
    }

    // Validar formato GUID (36 chars)
    if (str.length === 36 && this.GUID_REGEX.test(str)) {
      return {
        valid: true,
        format: 'GUID',
        length: 36
      };
    }

    // Validar formato Hex64 (64 chars - legado)
    if (str.length === 64 && this.HEX64_REGEX.test(str)) {
      return {
        valid: true,
        format: 'Hex64',
        length: 64
      };
    }

    // Formato não reconhecido
    return {
      valid: false,
      format: 'invalid',
      length: str.length,
      error: `Formato inválido (esperado: GUID 36 chars ou Hex 64 chars, recebido: ${str.length} chars)`
    };
  }

  /**
   * Valida HMAC key (deve ser Base64 válido e não-vazio)
   *
   * @param key Chave HMAC em Base64
   * @returns Resultado da validação
   */
  static validateHmacKey(key: string | null | undefined): ValidationResult {
    // Verificar se existe
    if (!key) {
      return {
        valid: false,
        error: 'HMAC key ausente'
      };
    }

    const str = String(key).trim();

    // Verificar se está vazio
    if (str.length === 0) {
      return {
        valid: false,
        length: 0,
        error: 'HMAC key vazio'
      };
    }

    // Validar formato Base64
    if (this.BASE64_REGEX.test(str)) {
      return {
        valid: true,
        length: str.length
      };
    }

    // Formato não reconhecido
    return {
      valid: false,
      length: str.length,
      error: `Formato inválido (esperado: Base64, recebido caracteres inválidos)`
    };
  }

  /**
   * Obtém diagnóstico completo dos tokens atuais
   *
   * Lê de window.sessionToken e window.sessionCrypto.hmacKeyB64
   *
   * @returns Diagnóstico detalhado para debug
   */
  static getDiagnostics(): TokenDiagnostics {
    const windowToken = (window as any)?.sessionToken;
    const windowHmac = (window as any)?.sessionCrypto?.hmacKeyB64;

    const tokenValidation = this.validateSessionToken(windowToken);
    const hmacValidation = this.validateHmacKey(windowHmac);

    return {
      sessionToken: {
        exists: !!windowToken,
        valid: tokenValidation.valid,
        format: tokenValidation.format || 'invalid',
        length: tokenValidation.length || 0,
        preview: windowToken ? `${String(windowToken).substring(0, 8)}...` : 'ausente'
      },
      hmacKey: {
        exists: !!windowHmac,
        valid: hmacValidation.valid,
        isBase64: hmacValidation.valid,
        length: hmacValidation.length || 0,
        preview: windowHmac ? `${String(windowHmac).substring(0, 8)}...` : 'ausente'
      },
      source: this.detectTokenSource()
    };
  }

  /**
   * Detecta a origem dos tokens (message, dom ou desconhecido)
   *
   * @returns Origem detectada
   */
  private static detectTokenSource(): 'message' | 'dom' | 'unknown' {
    // Se tem tokens válidos no DOM, assumir que vieram de algum lugar
    const hasValidToken = this.validateSessionToken((window as any)?.sessionToken).valid;
    const hasValidHmac = this.validateHmacKey((window as any)?.sessionCrypto?.hmacKeyB64).valid;

    if (hasValidToken && hasValidHmac) {
      // Se ambos estão válidos, assumir que vieram de message ou DOM
      // (não conseguimos distinguir a origem depois que já foram salvos)
      return 'dom';
    }

    return 'unknown';
  }

  /**
   * Valida se um objeto tem os campos necessários para bootstrap
   *
   * @param data Dados recebidos via message
   * @returns true se tem sessionToken e hmacKeyB64 válidos
   */
  static isValidBootstrapData(data: any): boolean {
    const tokenValidation = this.validateSessionToken(data?.sessionToken);
    const hmacValidation = this.validateHmacKey(data?.hmacKeyB64);

    return tokenValidation.valid && hmacValidation.valid;
  }

  /**
   * Cria preview seguro de um token (primeiros 8 chars + ...)
   *
   * @param token Token completo
   * @returns Preview sanitizado
   */
  static createPreview(token: string | null | undefined): string {
    if (!token) return 'ausente';
    const str = String(token);
    if (str.length <= 8) return `${str}...`;
    return `${str.substring(0, 8)}...`;
  }

  /**
   * Normaliza um token (trim e validação)
   *
   * @param token Token a normalizar
   * @returns Token normalizado ou null se inválido
   */
  static normalizeToken(token: string | null | undefined): string | null {
    if (!token) return null;
    const str = String(token).trim();
    const validation = this.validateSessionToken(str);
    return validation.valid ? str : null;
  }

  /**
   * Normaliza HMAC key (trim e validação)
   *
   * @param key HMAC key a normalizar
   * @returns Key normalizada ou null se inválida
   */
  static normalizeHmacKey(key: string | null | undefined): string | null {
    if (!key) return null;
    const str = String(key).trim();
    const validation = this.validateHmacKey(str);
    return validation.valid ? str : null;
  }
}
